import { inject, Injectable } from '@angular/core';
import { VehicleBaseView, BaseDataRequest } from './vehicle-base/VehicleBase';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';
import {
  TechnicalDataRequest,
  TechnicalDataView,
} from './vehicle-technical-data/VehicleTechnicalDataView';
import { HttpClient } from '@angular/common/http';
import {
  EquipmentRequest,
  EquipmentView,
} from './vehicle-equipment/VehicleEquipment';
import { SnackBarService } from '../snack-bar.service';
import { ReleaseRequest, ReleaseView } from './vehicle-release/VehicleRelease';
import { CarHubState } from './CarHubState';
import { TdsTranslationData } from '../TdsTranslationData';
import { SelectItem } from './SelectItem';
import { UcmimTranslationService } from '../translation.service';
import { APP_CONFIG } from '../app.config';

// ---------------------------------------------------------------------------
// Server response types (private to this service)
// ---------------------------------------------------------------------------

interface EquipmentServerResponse {
  equipmentUsedList: { code: string }[];
  equipmentNewList: {
    id: string;
    description: string;
    descriptionLong: string;
    enabled: boolean;
  }[];
}

interface VehicleTechnicalDataServerResponse {
  body: string;
  doors: number;
  driveTrain: string;
  emissionCo2: number;
  emissionClass: string;
  eurotax: number;
  fuel: string;
  hp30MinPower: string;
  hpHybrid: string;
  kw30MinPower: string;
  kwHybrid: string;
  maxNetPowerElectricHp: string;
  maxNetPowerElectricKw: string;
  combinedConsumption: number;
  modelCode: string;
  motorCapacity: number;
  powerKw: number;
  powerPs: number;
  seats: number;
  transmission: string;
  vin: string;
  weightGross: number;
  weightNet: number;
  carHubId: string;
}

interface VehicleDetailServerResponse {
  release: ReleaseView;
  model: string;
  base: VehicleBaseView;
  technicalData: VehicleTechnicalDataServerResponse;
  equipment: EquipmentServerResponse;
  failedValidations: [
    { clientKey: string; validationLevel: string; violatedCarHubField: string },
  ];
  vehicleId: string;
}

interface VehicleLog {
  dateTime: string;
  user: string;
  fieldName: string;
  oldValue: string;
  newValue: string;
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface VehicleDetailView {
  carHubId: string;
  release: ReleaseView;
  model: string;
  base: VehicleBaseView;
  technicalData: TechnicalDataView;
  equipment: EquipmentView;
  failedValidations: [
    { clientKey: string; validationLevel: string; violatedCarHubField: string },
  ];
  vehicleId: string;
}

export interface HeaderContent {
  content: string;
  fwLink?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Return the first available display text from a SelectItem, or '—'. */
function resolveText(item?: SelectItem): string {
  return item?.description
    ? item.description
    : item?.fuelTextShort
      ? item.fuelTextShort
      : '—';
}

/** Format value(s) with unit(s), or '—' when falsy. Supports single or dual-unit format. */
function withUnit(
  val1: unknown,
  unit1: string,
  val2?: unknown,
  unit2?: string,
): string {
  const format = (v: unknown, u: string) => (v ? `${v}${u}` : '—');
  if (val2 !== undefined && unit2 !== undefined) {
    if (!val1 && !val2) return '—';
    return `${format(val1, unit1)} / ${format(val2, unit2)}`;
  }
  return format(val1, unit1);
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class VehicleDetailService {
  private readonly httpClient = inject(HttpClient);
  private readonly translationService = inject(UcmimTranslationService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly appConfig = inject(APP_CONFIG);

  private readonly selectedVehicle = new BehaviorSubject<
    VehicleDetailView | undefined
  >(undefined);
  private readonly currency = new BehaviorSubject<string | undefined>(
    undefined,
  );

  get vehicleId(): string {
    return this.selectedVehicle.value!.vehicleId;
  }

  getSelectedVehicle(): Observable<VehicleDetailView> {
    return this.selectedVehicle.asObservable() as Observable<VehicleDetailView>;
  }

  getHeaderText(): Observable<HeaderContent> {
    return this.selectedVehicle.pipe(
      map((v) => ({
        content: v ? `${v.model} (${v.base.inventoryNumber})` : 'UCMIM',
        fwLink: v
          ? (this.buildFwLink(v.vehicleId, v.release.permissions) ?? undefined)
          : undefined,
      })),
    );
  }

  getBaseData(): Observable<VehicleBaseView> {
    return this.selectedVehicle.pipe(
      filter((v) => !!v),
      map((v) => v.base),
    );
  }

  getTechnicalData(): Observable<TechnicalDataView> {
    return this.selectedVehicle.pipe(
      filter((v) => !!v),
      map((v) => v.technicalData),
    );
  }

  getEquipment(): Observable<EquipmentView> {
    return this.selectedVehicle.pipe(
      filter((v) => !!v),
      map((v) => v.equipment),
      tap((e) => {
        e.equipmentNewList.sort((a, b) =>
          (a.description || '').localeCompare(b.description || ''),
        );
        e.equipmentUsedList.sort((a, b) =>
          (a.text || '').localeCompare(b.text || ''),
        );
      }),
    );
  }

  getReleaseView(): Observable<ReleaseView> {
    return this.selectedVehicle.pipe(
      filter((v) => !!v),
      map((v) => v.release),
    );
  }

  isShowReleaseTab(): Observable<boolean> {
    return this.selectedVehicle.pipe(
      filter((v) => !!v),
      map(
        (v) =>
          v.release.carHubState === CarHubState.READY ||
          v.release.carHubState === CarHubState.IN_CARHUB,
      ),
    );
  }

  getLogLazy(): Observable<VehicleLog[]> {
    return this.selectedVehicle.pipe(
      filter((v) => !!v),
      map((v) => v.carHubId),
      first(),
      switchMap((id) =>
        this.httpClient
          .get<VehicleLog[]>(`/api/vehicles/${id}/log`)
          .pipe(filter((logs) => !!logs)),
      ),
    );
  }

  getCurrency(): Observable<string> {
    return this.currency as Observable<string>;
  }

  setCurrencyFromBE() {
    this.httpClient
      .get('api/currency', { responseType: 'text' })
      .subscribe((data) => this.currency.next(data));
  }

  setSelectedVehicleId(selectedVehicleId: string | undefined) {
    if (!selectedVehicleId) {
      this.selectedVehicle.next(undefined);
      return;
    }
    this.httpClient
      .get<VehicleDetailServerResponse>(`/api/vehicles/${selectedVehicleId}`)
      .pipe(switchMap((r) => this.translateVehicleDetail(selectedVehicleId, r)))
      .subscribe((view) => this.selectedVehicle.next(view));
  }

  saveBaseData(base: BaseDataRequest) {
    this.removeEmptyStrings(base);
    this.saveAndRefresh((id) =>
      this.httpClient.put<VehicleDetailServerResponse>(
        `/api/vehicles/${id}/base`,
        base,
      ),
    );
  }

  saveTechnicalData(technical: TechnicalDataRequest) {
    this.saveAndRefresh((id) =>
      this.httpClient.put<VehicleDetailServerResponse>(
        `/api/vehicles/${id}/technical`,
        technical,
      ),
    );
  }

  sendVehicleToCarhub(releaseData: ReleaseRequest) {
    this.saveAndRefresh((id) =>
      this.httpClient.put<VehicleDetailServerResponse>(
        `/api/vehicles/${id}/carhub`,
        releaseData,
      ),
    );
  }

  updateEquipment(update: EquipmentRequest): Observable<void> {
    return this.selectedVehicle.pipe(
      first(),
      switchMap((v) =>
        this.httpClient
          .put<VehicleDetailServerResponse>(
            `/api/vehicles/${v!.carHubId}/equipment`,
            update,
          )
          .pipe(
            switchMap((r) => this.translateVehicleDetail(v!.carHubId, r)),
            map((translated) => this.selectedVehicle.next(translated)),
          ),
      ),
    );
  }

  public buildFwLink(
    vehicleId: string | number,
    permissions: Record<string, boolean>,
  ): string | null {
    if (!vehicleId || !permissions['int-dwa']) return null;
    const countrySegment =
      this.appConfig.stage === 'dev'
        ? ''
        : this.appConfig.country.toLowerCase();
    const paddedId = String(vehicleId).padStart(10, '0');
    return `${this.appConfig.fwBaseUrl}${countrySegment}/vehicle/${this.appConfig.country}${paddedId}/release`;
  }

  /** Shared save flow: get current carHubId → HTTP call → translate → update state → notify. */
  private saveAndRefresh(
    request: (carHubId: string) => Observable<VehicleDetailServerResponse>,
  ) {
    this.selectedVehicle
      .pipe(
        first(),
        map((v) => v!.carHubId),
        switchMap((id) =>
          request(id).pipe(
            switchMap((r) => this.translateVehicleDetail(id, r)),
          ),
        ),
      )
      .subscribe((updated) => {
        this.selectedVehicle.next(updated);
        this.snackBarService.showMessage('GENERAL.SAVE_SUCCESS');
      });
  }

  private translateVehicleDetail(
    carHubId: string,
    response: VehicleDetailServerResponse,
  ): Observable<VehicleDetailView> {
    return this.translationService.getTdsDataWhenReady().pipe(
      map((tds) => ({
        carHubId,
        release: response.release,
        model: response.model,
        base: response.base,
        technicalData: this.translateTechnicalData(response.technicalData, tds),
        equipment: this.translateEquipment(response.equipment, tds),
        failedValidations: response.failedValidations,
        vehicleId: response.vehicleId,
      })),
    );
  }

  private translateTechnicalData(
    td: VehicleTechnicalDataServerResponse,
    tds: TdsTranslationData,
  ): TechnicalDataView {
    const unit = (key: string) => this.translationService.getSelectedKey(key);
    return {
      body: resolveText(tds.bodies.find((b) => b.code === td.body)),
      doors: td.doors,
      driveTrain: resolveText(
        tds.driveTrains.find((d) => d.code === td.driveTrain),
      ),
      emissionCo2: td.emissionCo2,
      emissionClass: td.emissionClass,
      eurotax: td.eurotax + '',
      fuel: resolveText(tds.fuels.find((f) => f.code === td.fuel)),
      combinedConsumption: td.combinedConsumption,
      modelCode: td.modelCode,
      motorCapacity: withUnit(td.motorCapacity, unit('UNITS.CUBIC')),
      powerKwPs: withUnit(
        td.powerKw,
        unit('UNITS.KILO_WATT'),
        td.powerPs,
        unit('UNITS.HORSE_POWER'),
      ),
      power30MinKwPs: withUnit(
        td.kw30MinPower,
        unit('UNITS.KILO_WATT'),
        td.hp30MinPower,
        unit('UNITS.HORSE_POWER'),
      ),
      powerHybridKwPs: withUnit(
        td.kwHybrid,
        unit('UNITS.KILO_WATT'),
        td.hpHybrid,
        unit('UNITS.HORSE_POWER'),
      ),
      maxNetPowerKwPs: withUnit(
        td.maxNetPowerElectricKw,
        unit('UNITS.KILO_WATT'),
        td.maxNetPowerElectricHp,
        unit('UNITS.HORSE_POWER'),
      ),
      seats: td.seats,
      transmission: resolveText(
        tds.transmissions.find((t) => t.code === td.transmission),
      ),
      vin: td.vin,
      weightGross: withUnit(td.weightGross, unit('UNITS.KILO_GRAMM')),
      weightNet: withUnit(td.weightNet, unit('UNITS.KILO_GRAMM')),
      carHubId: td.carHubId,
    };
  }

  private translateEquipment(
    equipment: EquipmentServerResponse,
    tds: TdsTranslationData,
  ): EquipmentView {
    return {
      equipmentNewList: equipment.equipmentNewList.map((e) => ({
        id: e.id,
        description: e.description,
        descriptionLong:
          e.descriptionLong?.replace(/(?:\/#)/g, '\n') ?? e.descriptionLong,
        enabled: e.enabled,
      })),
      equipmentUsedList: equipment.equipmentUsedList.map((e) => ({
        code: e.code,
        text: resolveText(tds.equipments.find((te) => te.code === e.code)),
      })),
    };
  }

  // needed because otherwise the backend would validate the vin '' for a pattern although it should be null
  private removeEmptyStrings(object: any): void {
    for (const key of Object.keys(object)) {
      if (object[key] === '') object[key] = undefined;
    }
  }
}
